// Iter 306 — re-issue a corrected pro forma (version N) after a dispute.
import { useEffect, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { money } from "./engineShared";

export function ReissueDialog({ partnerKey, period, inquiry, onClose, onDone }) {
  const [state, setState] = useState(null);
  const [reason, setReason] = useState(inquiry ? `Following your ${inquiry.kind === "review" ? "review request" : "question"}: ` : "");
  const [sendEmail, setSendEmail] = useState(true);
  const [itemised, setItemised] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/commissions/partners/${encodeURIComponent(partnerKey)}/periods/${encodeURIComponent(period)}/workflow`)
      .then(({ data }) => setState(data)).catch(() => setState({}));
  }, [partnerKey, period]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/commissions/partners/${encodeURIComponent(partnerKey)}/periods/${encodeURIComponent(period)}/reissue`,
        { reason, inquiry_id: inquiry?.inquiry_id || "", send_email: sendEmail, itemised });
      const em = data.issue.email;
      toast.success(`Corrected pro forma v${data.issue.version} issued` + (em.requested ? (em.sent ? " and emailed" : em.sandbox ? " (email sandboxed)" : ` — email not sent: ${em.error}`) : ""));
      onDone?.(data.issue); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Re-issue failed"); } finally { setBusy(false); }
  };
  const acc = state?.acceptance;
  const next = state ? (state.version || 1) + 1 : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reissue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RotateCcw className="h-4 w-4 text-violet-700" /> Re-issue corrected pro forma — {period}</DialogTitle>
          <DialogDescription>
            The current numbers become version {next ?? "…"}. {acc ? `The acceptance by ${acc.accepted_by_name} (${money(acc.amount)}) is superseded, Accounting is told that entry is void, and ` : "The partner is asked to accept the corrected version, and "}
            the partner gets the corrected PDF with the reason below.
          </DialogDescription>
        </DialogHeader>
        {state && (
          <div className="rounded-md bg-slate-50 p-2 text-xs text-slate-600" data-testid="reissue-state">
            {acc ? <>Accepted {acc.accepted_at?.slice(0, 10)} for <b>{money(acc.amount)}</b></> : "Not currently accepted"}
            {state.issues?.length > 0 && <> · last corrected version v{state.issues[0].version} ({money(state.issues[0].new_total)})</>}
            {state.open_inquiries > 0 && <> · {state.open_inquiries} open inquir{state.open_inquiries === 1 ? "y" : "ies"}</>}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">What was corrected (the partner sees this)</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reissue-reason"
                      placeholder="e.g. Added the missing Kingston Midas renewal and fixed the rate on acme.ca" />
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={sendEmail} onCheckedChange={setSendEmail} data-testid="reissue-send-email" /> Email the corrected PDF to the partner contact</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={itemised} onCheckedChange={setItemised} data-testid="reissue-itemised" /> Attach the itemised version</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reissue-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} className="bg-violet-700 text-white hover:bg-violet-800" data-testid="reissue-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Issue version ${next ?? ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
